import { ITopLeagueFeedResponse, ISportFeedResponse } from "@finbackoffice/websocket-client";
import {
    Dispatch,
    FC,
    PropsWithChildren,
    SetStateAction,
    createContext,
    useMemo,
    useState,
} from "react";

export type Feed = ISportFeedResponse[];
type Rid = string;

// ---------------------------------------------------------------------------
// State shapes
// ---------------------------------------------------------------------------

type FeedStatePrematchUpcoming = Omit<FeedStatePrematch, "topLeagues">;

type FeedStatePrematch = {
    loading: boolean;
    sports: Feed;
    topLeagues: ITopLeagueFeedResponse[];
    error: string;
    rid: Rid;
};

type FeedStateLive = {
    loading: boolean;
    sports: Feed;
    topLeagues: ITopLeagueFeedResponse[];
    totalGames: number;
    rid: Rid;
};

type FeedStateResults = {
    sports: Feed;
    loading: boolean;
    rid: Rid;
};

// ---------------------------------------------------------------------------
// LiveFeedContext
// ---------------------------------------------------------------------------

export type ILiveFeedContext = {
    liveFeed: Feed;
    liveIsLoading: boolean;
    liveFeedCount: number;
    hasLiveFeedGames: boolean;
    liveTopLeagues: ITopLeagueFeedResponse[];
    liveTopLeaguesGamesCount: number;
    setLive: Dispatch<SetStateAction<FeedStateLive>>;
};

export const LiveFeedContext = createContext<ILiveFeedContext>(null as any);

// ---------------------------------------------------------------------------
// FeedRidContext
// ---------------------------------------------------------------------------

export type IFeedRidContext = {
    liveRid: string;
    prematchRid: string;
    prematchUpcomingRid: string;
    resultsRid: string;
};

export const FeedRidContext = createContext<IFeedRidContext>({
    liveRid: "",
    prematchRid: "",
    prematchUpcomingRid: "",
    resultsRid: "",
});

// ---------------------------------------------------------------------------
// PrematchFeedContext
// ---------------------------------------------------------------------------

export type IPrematchFeedContext = {
    prematchFeed: Feed;
    prematchFeedError: string;
    prematchIsLoading: boolean;
    hasPrematchSports: boolean;
    prematchTopLeagues: ITopLeagueFeedResponse[];
    setPrematch: Dispatch<SetStateAction<FeedStatePrematch>>;
};

export const PrematchFeedContext = createContext<IPrematchFeedContext>(null as any);

// ---------------------------------------------------------------------------
// PrematchUpcomingFeedContext
// ---------------------------------------------------------------------------

export type IPrematchUpcomingFeedContext = {
    prematchUpcomingFeed: Feed;
    prematchUpcomingError: string;
    prematchUpcomingIsLoading: boolean;
    hasPrematchUpcomingSports: boolean;
    setPrematchUpcoming: Dispatch<SetStateAction<FeedStatePrematchUpcoming>>;
};

export const PrematchUpcomingFeedContext = createContext<IPrematchUpcomingFeedContext>(null as any);

// ---------------------------------------------------------------------------
// ResultsFeedContext
// ---------------------------------------------------------------------------

export type IResultsFeedContext = {
    resultsFeed: Feed;
    resultsIsLoading: boolean;
    hasResultsSports: boolean;
    setResults: Dispatch<SetStateAction<FeedStateResults>>;
};

export const ResultsFeedContext = createContext<IResultsFeedContext>(null as any);

// ---------------------------------------------------------------------------
// FeedProvider
// ---------------------------------------------------------------------------

export const FeedProvider: FC<PropsWithChildren> = ({ children }) => {
    const [prematch, setPrematch] = useState<FeedStatePrematch>({
        loading: true,
        sports: [],
        topLeagues: [],
        error: "",
        rid: "",
    });
    const [prematchUpcoming, setPrematchUpcoming] = useState<FeedStatePrematchUpcoming>({
        loading: true,
        sports: [],
        error: "",
        rid: "",
    });
    const [live, setLive] = useState<FeedStateLive>({
        loading: false,
        sports: [],
        topLeagues: [],
        totalGames: 0,
        rid: "",
    });
    const [results, setResults] = useState<FeedStateResults>({
        loading: false,
        sports: [],
        rid: "",
    });

    const liveTopLeaguesGamesCount = useMemo(
        () => live.topLeagues.reduce((acc, league) => acc + league.games_count, 0),
        [live.topLeagues],
    );

    const liveValue = useMemo<ILiveFeedContext>(
        () => ({
            setLive,
            liveFeed: live.sports,
            liveIsLoading: live.loading,
            liveFeedCount: live.totalGames,
            hasLiveFeedGames: Boolean(live.totalGames),
            liveTopLeagues: live.topLeagues,
            liveTopLeaguesGamesCount,
        }),
        [live.loading, live.sports, live.topLeagues, live.totalGames, liveTopLeaguesGamesCount],
    );

    const prematchValue = useMemo<IPrematchFeedContext>(
        () => ({
            setPrematch,
            prematchFeed: prematch.sports,
            prematchFeedError: prematch.error,
            prematchIsLoading: prematch.loading,
            hasPrematchSports: Boolean(prematch.sports.length),
            prematchTopLeagues: prematch.topLeagues,
        }),
        [prematch.error, prematch.loading, prematch.sports, prematch.topLeagues],
    );

    const prematchUpcomingValue = useMemo<IPrematchUpcomingFeedContext>(
        () => ({
            setPrematchUpcoming,
            prematchUpcomingFeed: prematchUpcoming.sports,
            prematchUpcomingError: prematchUpcoming.error,
            prematchUpcomingIsLoading: prematchUpcoming.loading,
            hasPrematchUpcomingSports: Boolean(prematchUpcoming.sports.length),
        }),
        [prematchUpcoming.error, prematchUpcoming.loading, prematchUpcoming.sports],
    );

    const resultsValue = useMemo<IResultsFeedContext>(
        () => ({
            setResults,
            resultsFeed: results.sports,
            resultsIsLoading: results.loading,
            hasResultsSports: Boolean(results.sports.length),
        }),
        [results.loading, results.sports],
    );

    const ridValue = useMemo<IFeedRidContext>(
        () => ({
            liveRid: live.rid,
            prematchRid: prematch.rid,
            prematchUpcomingRid: prematchUpcoming.rid,
            resultsRid: results.rid,
        }),
        [live.rid, prematch.rid, prematchUpcoming.rid, results.rid],
    );

    return (
        <FeedRidContext.Provider value={ridValue}>
            <LiveFeedContext.Provider value={liveValue}>
                <PrematchFeedContext.Provider value={prematchValue}>
                    <PrematchUpcomingFeedContext.Provider value={prematchUpcomingValue}>
                        <ResultsFeedContext.Provider value={resultsValue}>
                            {children}
                        </ResultsFeedContext.Provider>
                    </PrematchUpcomingFeedContext.Provider>
                </PrematchFeedContext.Provider>
            </LiveFeedContext.Provider>
        </FeedRidContext.Provider>
    );
};
